import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ChevronDown, Info, RefreshCcw, Save, Trophy } from 'lucide-react'
import { Button, Card } from '@/components/ui'
import { useAuthStore } from '@/store'
import { useManagementCompanySimple } from '@/hooks/useManagementCompanySimple'
import type {
  HackathonWeekAssignmentRow,
  HackathonWednesdayPulseRow,
  PersonalManagerListItem,
} from '@/types'
import { getSupabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import { todayMskISO } from '@/lib/personalManagerTasks'
import { formatCurrency } from '@/lib/utils'
import type { MetricWeek } from '@/hooks/useMetricWeeks'
import {
  getHackathonWednesdayPulse,
  listHackathonWeekAssignments,
  listPersonalManagers,
  saveHackathonWeekAssignments,
} from '@/services/personalManagerService'

type GroupKey = 1 | 2 | 3 | 4 | 5 | 'excluded'
type ResultsScope = 'all' | 1 | 2 | 3 | 4 | 5
type RatingBar = {
  id: string
  label: string
  value: number
  meta?: string
}
type StudioSubscriptionType = 'REGULAR' | 'SUBSCRIPTION' | 'MIXED'
type AssignmentSegment = HackathonWeekAssignmentRow['segment']

type StudioMetaRow = {
  partnerNumber: number
  studioInternalId: number
  studioNumber: string
  cityName: string | null
  subscriptionType: StudioSubscriptionType
}

type PartnerRatingRow = {
  partner_number: number
  persons_fio: string
  revenue_rub: number
  place_in_group: number
  place_overall: number
}

type SummaryMetrics = {
  planWeekRub: number
  factWeekRub: number
  planTodayRub: number
  factTodayRub: number
  openStudiosCount: number
}

const GROUP_DEFS: Array<{ key: GroupKey; title: string; hint: string }> = [
  { key: 1, title: 'Группа 1', hint: '3 студии на абонементах и больше, средняя выручка на одну студию за неделю от 200 000 ₽.' },
  { key: 2, title: 'Группа 2', hint: '1-2 студии на абонементах, средняя выручка на одну студию за неделю от 200 000 ₽.' },
  { key: 3, title: 'Группа 3', hint: '3 студии на абонементах и больше, средняя выручка на одну студию за неделю меньше 200 000 ₽.' },
  { key: 4, title: 'Группа 4', hint: '1-2 студии на абонементах, средняя выручка на одну студию за неделю меньше 200 000 ₽.' },
  { key: 5, title: 'Группа 5', hint: 'Студии на подписках, включая MIXED. Если у франчайзи есть оба сегмента, участие в основной группе и группе 5 настраивается независимо.' },
  { key: 'excluded', title: 'Не участвуют', hint: 'Исключается только выбранный сегмент. Второй сегмент того же франчайзи может продолжать участвовать.' },
]
const REVENUE_HINT = 'Средняя недельная выручка на одну студию этого сегмента за последние 4 завершённые недели.'

function isSubscriptionStudio(subscriptionType: StudioSubscriptionType): boolean {
  return subscriptionType === 'SUBSCRIPTION' || subscriptionType === 'MIXED'
}


function formatCitiesList(cities: string[]): string {
  const uniqueCities = Array.from(new Set(cities.map((city) => city.trim()).filter(Boolean)))
  if (uniqueCities.length === 0) return 'Город не указан'
  return uniqueCities.join(', ')
}

function getRankBadgeClass(place: number): string {
  if (place === 1) return 'border-primary/40 bg-primary/10 text-primary'
  if (place === 2) return 'border-border bg-muted text-foreground'
  if (place === 3) return 'border-orange-500/30 bg-orange-500/10 text-orange-500'
  return 'border-border bg-background text-muted-foreground'
}

function formatWeekRange(start: string, end: string): string {
  const startDate = new Date(`${start}T00:00:00`)
  const endDate = new Date(`${end}T00:00:00`)
  return `${startDate.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })} - ${endDate.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}`
}

function formatDateVerbose(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function formatStudioCount(count: number): string {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) return `${count} студия`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} студии`
  return `${count} студий`
}

function managerDisplayName(manager: PersonalManagerListItem): string {
  return manager.manager_name || manager.tg_username || manager.manager_user_id.slice(0, 8)
}

function assignmentColumnKey(row: HackathonWeekAssignmentRow): GroupKey {
  if (!row.is_participating || row.group_number == null) return 'excluded'
  return row.group_number as 1 | 2 | 3 | 4 | 5
}

type AssignmentPartnerCard = {
  partnerNumber: number
  personsFio: string
  rows: HackathonWeekAssignmentRow[]
}

function groupAssignmentRowsByPartner(rows: HackathonWeekAssignmentRow[]): AssignmentPartnerCard[] {
  const byPartner = new Map<number, AssignmentPartnerCard>()

  for (const row of rows) {
    const current = byPartner.get(row.partner_number)
    if (current) {
      current.rows.push(row)
      continue
    }

    byPartner.set(row.partner_number, {
      partnerNumber: row.partner_number,
      personsFio: row.persons_fio,
      rows: [row],
    })
  }

  return Array.from(byPartner.values())
    .map((card) => ({
      ...card,
      rows: card.rows.slice().sort((a, b) => a.segment.localeCompare(b.segment)),
    }))
    .sort((a, b) => a.personsFio.localeCompare(b.personsFio, 'ru') || a.partnerNumber - b.partnerNumber)
}

function toComparableSnapshot(rows: HackathonWeekAssignmentRow[]) {
  return rows
    .map((row) => ({
      partner_number: row.partner_number,
      segment: row.segment,
      is_participating: row.is_participating,
      group_number: row.group_number,
      assignment_source: row.assignment_source,
    }))
    .sort((a, b) => a.partner_number - b.partner_number || a.segment.localeCompare(b.segment))
}

function snapshotsEqual(a: HackathonWeekAssignmentRow[], b: HackathonWeekAssignmentRow[]): boolean {
  return JSON.stringify(toComparableSnapshot(a)) === JSON.stringify(toComparableSnapshot(b))
}

function formatHackathonError(error: unknown): string {
  const raw = error instanceof Error ? error.message : String(error)
  if (raw.includes('public.list_hackathon_week_assignments') && raw.includes('PGRST202')) {
    return 'Функции хакатона еще не появились в базе. Нужно применить миграцию экрана хакатона и обновить schema cache.'
  }
  if (raw.includes('public.list_hackathon_week_results') && raw.includes('PGRST202')) {
    return 'Функции рейтинга хакатона еще не появились в базе. Нужно применить миграцию экрана хакатона и обновить schema cache.'
  }
  if (raw.includes('public.save_hackathon_week_assignments') && raw.includes('PGRST202')) {
    return 'Сохранение пока недоступно: функция еще не появилась в базе. Нужно применить миграцию экрана хакатона.'
  }
  if (raw.includes('public.get_uk_wednesday_revenue_pulse_payload') && raw.includes('PGRST202')) {
    return 'Функция сводки хакатона еще не появилась в базе. Нужно применить миграцию и обновить schema cache.'
  }
  return raw
}

function RatingGauge({
  percent,
  title,
}: {
  percent: number
  title: string
}) {
  const displayed = Math.max(0, percent)
  const ringPercent = Math.min(100, displayed)
  const gaugeColor = displayed >= 100
    ? '#16a34a'
    : displayed >= 50
      ? '#f5b800'
      : '#dc2626'

  // A 270-degree circular gauge with one fixed 90-degree opening at the bottom.
  // Both the track and progress use the same circle geometry, which avoids
  // stray end-caps/dots and keeps the arc perfectly symmetrical.
  const radius = 42
  const circumference = 2 * Math.PI * radius
  const visibleArc = circumference * 0.75
  const progressArc = visibleArc * (ringPercent / 100)

  return (
    <div className="relative h-[112px] w-[146px] shrink-0" aria-label={`${title}: ${Math.round(displayed)}%`}>
      <svg viewBox="0 0 140 112" className="absolute inset-0 h-full w-full" aria-hidden="true">
        <circle
          cx="70"
          cy="54"
          r={radius}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={`${visibleArc} ${circumference - visibleArc}`}
          transform="rotate(135 70 54)"
        />
        {ringPercent > 0 && (
          <circle
            cx="70"
            cy="54"
            r={radius}
            fill="none"
            stroke={gaugeColor}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={`${progressArc} ${circumference - progressArc}`}
            transform="rotate(135 70 54)"
            className="transition-[stroke,stroke-dasharray] duration-300"
          />
        )}
      </svg>

      <div className="absolute inset-x-0 top-[39px] text-center">
        <div className="text-[28px] font-bold leading-none tabular-nums" style={{ color: gaugeColor }}>
          {Math.round(displayed)}%
        </div>
      </div>
      <div className="absolute inset-x-0 top-[91px] text-center text-[10px] font-medium leading-none text-muted-foreground">
        {title}
      </div>
    </div>
  )
}

export function PersonalManagersHackathonPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { getAccessLevel } = useManagementCompanySimple(user?.id)

  const [weeks, setWeeks] = useState<MetricWeek[]>([])
  const [selectedWeekId, setSelectedWeekId] = useState<number | null>(null)
  const [rows, setRows] = useState<HackathonWeekAssignmentRow[]>([])
  const [initialRows, setInitialRows] = useState<HackathonWeekAssignmentRow[]>([])
  const [managers, setManagers] = useState<PersonalManagerListItem[]>([])
  const [selectedManagerId, setSelectedManagerId] = useState('all')
  const [resultsScope, setResultsScope] = useState<ResultsScope>('all')
  const [pulse, setPulse] = useState<HackathonWednesdayPulseRow | null>(null)
  const [studioMetaRows, setStudioMetaRows] = useState<StudioMetaRow[]>([])
  const [liveStudioRevenue, setLiveStudioRevenue] = useState<Array<{ studioNumber: string; revenueRub: number }>>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [resultsLoading, setResultsLoading] = useState(false)
  const [pulseError, setPulseError] = useState<string | null>(null)
  const [studioMetaError, setStudioMetaError] = useState<string | null>(null)
  const [studioRevenueError, setStudioRevenueError] = useState<string | null>(null)
  const [studioMetaLoading, setStudioMetaLoading] = useState(false)
  const [studioRevenueLoading, setStudioRevenueLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const [isAdvanced, setIsAdvanced] = useState<boolean | null>(null)
  const [distributionOpen, setDistributionOpen] = useState(false)
  const canEdit = isAdvanced === true

  // The hackathon business day is Moscow time. Using the browser's local date
  // here could hide/show Wednesday results incorrectly around midnight for users
  // outside MSK.
  const todayIso = todayMskISO()

  const reload = async () => {
    setLoading(true)
    setSaveMessage(null)
    try {
      const supabase = getSupabase()
      const { data: currentWeekData, error: currentWeekError } = await withAuthRecovery(() =>
        supabase
          .from('metric_weeks')
          .select('week_id, period_id, week_number_in_period, week_start, week_end, week_len_days')
          .lte('week_start', todayIso)
          .gte('week_end', todayIso)
          .order('week_start', { ascending: false })
          .limit(1)
      )
      if (currentWeekError) throw currentWeekError

      let metricWeeks = ((currentWeekData ?? []) as MetricWeek[])
      if (metricWeeks.length === 0) {
        const { data: fallbackWeekData, error: fallbackWeekError } = await withAuthRecovery(() =>
          supabase
            .from('metric_weeks')
            .select('week_id, period_id, week_number_in_period, week_start, week_end, week_len_days')
            .lte('week_start', todayIso)
            .order('week_start', { ascending: false })
            .limit(1)
        )
        if (fallbackWeekError) throw fallbackWeekError
        metricWeeks = ((fallbackWeekData ?? []) as MetricWeek[])
      }

      setWeeks(metricWeeks)

      const resolvedWeekId = metricWeeks[0]?.week_id ?? null

      setSelectedWeekId(resolvedWeekId)

      const [managerList, assignmentRows] = await Promise.all([
        listPersonalManagers(),
        resolvedWeekId ? listHackathonWeekAssignments(resolvedWeekId) : Promise.resolve([]),
      ])

      setManagers(managerList)
      setRows(assignmentRows)
      setInitialRows(assignmentRows)
      setError(null)
    } catch (err) {
      setError(formatHackathonError(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    reload()
  }, [])

  useEffect(() => {
    let cancelled = false
    if (!user?.id) return
    getAccessLevel().then((level) => {
      if (!cancelled) setIsAdvanced(level === 'advanced')
    })
    return () => { cancelled = true }
  }, [user?.id, getAccessLevel])

  useEffect(() => {
    if (selectedWeekId == null) return
    listHackathonWeekAssignments(selectedWeekId)
      .then((data) => {
        setRows(data)
        setInitialRows(data)
        setSaveMessage(null)
        setError(null)
      })
      .catch((err) => setError(formatHackathonError(err)))
  }, [selectedWeekId])

  const studioMetaPartnerNumbers = useMemo<number[]>(
    () => Array.from(new Set<number>(rows.map((row) => row.partner_number)))
      .filter((value) => Number.isFinite(value))
      .sort((a, b) => a - b),
    [rows]
  )
  const studioMetaPartnerNumbersKey = studioMetaPartnerNumbers.join(',')

  useEffect(() => {
    const partnerNumbers = studioMetaPartnerNumbers
    if (partnerNumbers.length === 0) {
      setStudioMetaRows([])
      setStudioMetaError(null)
      setStudioMetaLoading(false)
      return
    }

    let cancelled = false
    setStudioMetaLoading(true)
    const loadStudioMeta = async () => {
      try {
        const supabase = getSupabase()
        const ownershipRows: any[] = []
        const pageSize = 1000
        for (let from = 0; ; from += pageSize) {
          const { data, error } = await withAuthRecovery(() =>
            (supabase as any)
              .from('studio_ownerships')
              .select(`
                start_date,
                end_date,
                ownership_phase,
                contract_series!inner(partner_number),
                studios!inner(
                  studio_internal_id,
                  studio_number,
                  status_code,
                  city:city_id(city_name)
                )
              `)
              .in('contract_series.partner_number', partnerNumbers)
              .order('ownership_id', { ascending: true })
              .range(from, from + pageSize - 1)
          )
          if (error) throw error
          const batch = (data ?? []) as any[]
          ownershipRows.push(...batch)
          if (batch.length < pageSize) break
        }

        const dedup = new Map<string, StudioMetaRow>()
        for (const rawRow of ownershipRows) {
          const partnerNumberRaw = Array.isArray(rawRow.contract_series)
            ? rawRow.contract_series[0]?.partner_number
            : rawRow.contract_series?.partner_number
          const studio = Array.isArray(rawRow.studios) ? rawRow.studios[0] : rawRow.studios
          const partnerNumber = Number(partnerNumberRaw)
          const studioInternalId = Number(studio?.studio_internal_id ?? 0)
          const studioNumber = studio?.studio_number ? String(studio.studio_number) : ''
          const cityName = studio?.city?.city_name ? String(studio.city.city_name) : null
          const statusCode = String(studio?.status_code ?? '')
          const phase = String(rawRow.ownership_phase ?? '')
          const startDate = rawRow.start_date ? String(rawRow.start_date).slice(0, 10) : null
          const endDate = rawRow.end_date ? String(rawRow.end_date).slice(0, 10) : null
          const isActive = (startDate == null || startDate <= todayIso) && (endDate == null || todayIso <= endDate)

          if (!Number.isFinite(partnerNumber) || !studioNumber || statusCode !== 'OPEN') continue
          if (!isActive || !['OWNED', 'FOR_SALE'].includes(phase)) continue

          dedup.set(`${partnerNumber}-${studioNumber}`, {
            partnerNumber,
            studioInternalId,
            studioNumber,
            cityName,
            subscriptionType: 'REGULAR',
          })
        }

        const studioInternalIds = Array.from(new Set(
          Array.from(dedup.values())
            .map((studio) => studio.studioInternalId)
            .filter((id) => Number.isFinite(id) && id > 0)
        ))
        if (studioInternalIds.length > 0) {
          const { data: subscriptionRows, error: subscriptionError } = await withAuthRecovery(() =>
            (supabase as any)
              .from('studio_subscription_settings')
              .select('studio_internal_id, subscription_type')
              .in('studio_internal_id', studioInternalIds)
          )
          if (subscriptionError) throw subscriptionError

          const subscriptionTypeByStudioId = new Map<number, StudioSubscriptionType>()
          for (const rawSubscription of ((subscriptionRows ?? []) as any[])) {
            const studioInternalId = Number(rawSubscription.studio_internal_id)
            const rawType = String(rawSubscription.subscription_type ?? 'REGULAR')
            const subscriptionType: StudioSubscriptionType = rawType === 'SUBSCRIPTION' || rawType === 'MIXED'
              ? rawType
              : 'REGULAR'
            if (Number.isFinite(studioInternalId)) subscriptionTypeByStudioId.set(studioInternalId, subscriptionType)
          }

          for (const studio of dedup.values()) {
            studio.subscriptionType = subscriptionTypeByStudioId.get(studio.studioInternalId) ?? 'REGULAR'
          }
        }

        if (!cancelled) {
          setStudioMetaError(null)
          setStudioMetaRows(Array.from(dedup.values()).sort((a, b) => {
            const partnerDiff = a.partnerNumber - b.partnerNumber
            if (partnerDiff !== 0) return partnerDiff
            return a.studioNumber.localeCompare(b.studioNumber, 'ru', { numeric: true })
          }))
        }
      } catch (err) {
        console.error('Failed to load studio meta for hackathon page', err)
        if (!cancelled) {
          setStudioMetaRows([])
          setStudioMetaError(formatHackathonError(err))
        }
      } finally {
        if (!cancelled) setStudioMetaLoading(false)
      }
    }

    loadStudioMeta()
    return () => { cancelled = true }
  }, [studioMetaPartnerNumbersKey, todayIso])

  const selectedWeek = useMemo(
    () => weeks.find((week) => week.week_id === selectedWeekId) ?? null,
    [weeks, selectedWeekId]
  )

  const partnerNameByNumber = useMemo(
    () => new Map(rows.map((row) => [row.partner_number, row.persons_fio])),
    [rows]
  )
  const assignmentByPartnerSegment = useMemo(() => {
    const next = new Map<string, HackathonWeekAssignmentRow>()
    for (const row of rows) {
      next.set(`${row.partner_number}:${row.segment}`, row)
    }
    return next
  }, [rows])
  const studiosByPartner = useMemo(() => {
    const next = new Map<number, StudioMetaRow[]>()
    for (const row of studioMetaRows) {
      const bucket = next.get(row.partnerNumber) ?? []
      bucket.push(row)
      next.set(row.partnerNumber, bucket)
    }
    return next
  }, [studioMetaRows])
  const partnerCitiesByNumber = useMemo(() => {
    const next = new Map<number, string>()
    for (const [partnerNumber, studios] of studiosByPartner.entries()) {
      next.set(partnerNumber, formatCitiesList(studios.map((studio) => studio.cityName ?? '').filter(Boolean)))
    }
    return next
  }, [studiosByPartner])
  const studioMetaByNumber = useMemo(() => {
    const next = new Map<string, StudioMetaRow>()
    for (const row of studioMetaRows) {
      if (!next.has(row.studioNumber)) next.set(row.studioNumber, row)
    }
    return next
  }, [studioMetaRows])

  const managerOptions = useMemo(() => {
    const visibleManagerIds = new Set(rows.map((row) => row.manager_user_id).filter(Boolean))
    return managers
      .filter((manager) => visibleManagerIds.has(manager.manager_user_id))
      .sort((a, b) => managerDisplayName(a).localeCompare(managerDisplayName(b), 'ru'))
  }, [managers, rows])

  const filteredRows = useMemo(() => {
    if (selectedManagerId === 'all') return rows
    return rows.filter((row) => row.manager_user_id === selectedManagerId)
  }, [rows, selectedManagerId])

  const groupedRows = useMemo(() => {
    const bucket = new Map<GroupKey, HackathonWeekAssignmentRow[]>()
    for (const def of GROUP_DEFS) bucket.set(def.key, [])
    for (const row of filteredRows) {
      bucket.get(assignmentColumnKey(row))?.push(row)
    }
    for (const value of bucket.values()) {
      value.sort((a, b) => a.persons_fio.localeCompare(b.persons_fio, 'ru')
        || a.partner_number - b.partner_number
        || a.segment.localeCompare(b.segment))
    }
    return bucket
  }, [filteredRows])

  const hasUnsavedChanges = useMemo(
    () => rows.some((row) => !row.has_saved_assignment) || !snapshotsEqual(rows, initialRows),
    [rows, initialRows]
  )
  // Неделя без своего сохранения открывается решениями последней сохранённой недели (сервер).
  const carriedFromRow = useMemo(() => rows.find((row) => row.carried_from_week_start) ?? null, [rows])
  const hasManualRows = useMemo(() => rows.some((row) => row.assignment_source === 'manual'), [rows])
  const participantsCount = useMemo(
    () => new Set(rows.filter((row) => row.is_participating).map((row) => row.partner_number)).size,
    [rows]
  )
  const excludedCount = useMemo(() => rows.filter((row) => !row.is_participating).length, [rows])
  const manualCount = useMemo(() => rows.filter((row) => row.assignment_source === 'manual').length, [rows])

  const isWednesdayViewActive = Boolean(selectedWeek && todayIso === selectedWeek.week_end)

  const studioGroupByNumber = useMemo(() => {
    const next = new Map<string, 1 | 2 | 3 | 4 | 5>()
    for (const studio of studioMetaRows) {
      const segment: AssignmentSegment = isSubscriptionStudio(studio.subscriptionType) ? 'subscription' : 'regular'
      const assignment = assignmentByPartnerSegment.get(`${studio.partnerNumber}:${segment}`)
      if (!assignment?.is_participating || assignment.group_number == null) continue
      next.set(studio.studioNumber, assignment.group_number as 1 | 2 | 3 | 4 | 5)
    }
    return next
  }, [assignmentByPartnerSegment, studioMetaRows])
  const resultPartnerCitiesByNumber = useMemo(() => {
    if (resultsScope === 'all') return partnerCitiesByNumber

    const citiesByPartner = new Map<number, string[]>()
    for (const studio of studioMetaRows) {
      if (studioGroupByNumber.get(studio.studioNumber) !== resultsScope) continue
      const bucket = citiesByPartner.get(studio.partnerNumber) ?? []
      if (studio.cityName) bucket.push(studio.cityName)
      citiesByPartner.set(studio.partnerNumber, bucket)
    }

    const next = new Map<number, string>()
    for (const [partnerNumber, cities] of citiesByPartner.entries()) {
      next.set(partnerNumber, formatCitiesList(cities))
    }
    return next
  }, [partnerCitiesByNumber, resultsScope, studioGroupByNumber, studioMetaRows])

  // Rankings are assembled from the same live studio revenue that powers the studio Top-10.
  // This is important for mixed portfolios: regular studios contribute only to groups 1-4,
  // while SUBSCRIPTION/MIXED studios of the same franchisee contribute only to group 5.
  const displayedPartnerResultsScoped = useMemo<PartnerRatingRow[]>(() => {
    if (!isWednesdayViewActive || liveStudioRevenue.length === 0) return []
    const revenueByPartner = new Map<number, number>()
    for (const studio of liveStudioRevenue) {
      const meta = studioMetaByNumber.get(studio.studioNumber)
      if (!meta) continue
      const groupNumber = studioGroupByNumber.get(studio.studioNumber)
      if (groupNumber == null) continue
      if (resultsScope !== 'all' && groupNumber !== resultsScope) continue

      revenueByPartner.set(meta.partnerNumber, (revenueByPartner.get(meta.partnerNumber) ?? 0) + studio.revenueRub)
    }

    return Array.from(revenueByPartner.entries())
      .map(([partnerNumber, revenueRub]) => ({
        partner_number: partnerNumber,
        persons_fio: partnerNameByNumber.get(partnerNumber) ?? `Франчайзи #${partnerNumber}`,
        revenue_rub: revenueRub,
        place_in_group: 0,
        place_overall: 0,
      }))
      .sort((a, b) => b.revenue_rub - a.revenue_rub || a.partner_number - b.partner_number)
      .map((row, index) => ({
        ...row,
        place_in_group: index + 1,
        place_overall: index + 1,
      }))
  }, [
    isWednesdayViewActive,
    liveStudioRevenue,
    partnerNameByNumber,
    resultsScope,
    studioGroupByNumber,
    studioMetaByNumber,
  ])

  const displayedStudioBars = useMemo<RatingBar[]>(() => {
    if (!isWednesdayViewActive || liveStudioRevenue.length === 0) return []
    const liveBars = liveStudioRevenue
      .map((studio) => {
        const meta = studioMetaByNumber.get(studio.studioNumber)
        if (!meta) return null
        const groupNumber = studioGroupByNumber.get(studio.studioNumber)
        if (groupNumber == null) return null
        if (resultsScope !== 'all' && groupNumber !== resultsScope) return null
        return {
          id: studio.studioNumber,
          label: `Студия ${studio.studioNumber}`,
          value: studio.revenueRub,
          meta: [
            meta.cityName ?? null,
            partnerNameByNumber.get(meta.partnerNumber) ?? null,
          ].filter(Boolean).join(' · '),
        }
      })
      .filter(Boolean) as RatingBar[]
    return liveBars.sort((a, b) => b.value - a.value || a.label.localeCompare(b.label, 'ru'))
  }, [
    isWednesdayViewActive,
    liveStudioRevenue,
    partnerNameByNumber,
    resultsScope,
    studioGroupByNumber,
    studioMetaByNumber,
  ])
  const topPartnerRows = useMemo(() => displayedPartnerResultsScoped.slice(0, 10), [displayedPartnerResultsScoped])
  const topStudioRows = useMemo(() => displayedStudioBars.slice(0, 10), [displayedStudioBars])
  const partnerChartMax = useMemo(
    () => displayedPartnerResultsScoped.reduce((max, row) => Math.max(max, row.revenue_rub), 0),
    [displayedPartnerResultsScoped]
  )
  const studioChartMax = useMemo(
    () => displayedStudioBars.reduce((max, row) => Math.max(max, row.value), 0),
    [displayedStudioBars]
  )
  const rankingLoading = studioMetaLoading || studioRevenueLoading
  const rankingError = studioMetaError ?? studioRevenueError
  const summaryMetrics = useMemo<SummaryMetrics | null>(() => {
    if (!isWednesdayViewActive || !pulse) return null

    const planTodayRub = Math.max(pulse.plan_week_rub - Math.max(pulse.fact_week_rub - pulse.today_revenue_rub, 0), 0)
    return {
      planWeekRub: pulse.plan_week_rub,
      factWeekRub: pulse.fact_week_rub,
      planTodayRub,
      factTodayRub: pulse.today_revenue_rub,
      openStudiosCount: pulse.open_studios_count,
    }
  }, [isWednesdayViewActive, pulse])
  const dayGaugePct = useMemo(() => {
    if (!summaryMetrics) return 0
    if (summaryMetrics.planTodayRub <= 0) return 100
    return Math.max(0, (summaryMetrics.factTodayRub / summaryMetrics.planTodayRub) * 100)
  }, [summaryMetrics])
  const weekCompletionPct = useMemo(() => {
    if (!summaryMetrics) return 0
    if (summaryMetrics.planWeekRub <= 0) return 100
    return Math.max(0, (summaryMetrics.factWeekRub / summaryMetrics.planWeekRub) * 100)
  }, [summaryMetrics])
  const dayTargetPerStudioRub = useMemo(() => {
    if (!summaryMetrics || summaryMetrics.openStudiosCount <= 0) return 0
    return Math.round(summaryMetrics.planTodayRub / summaryMetrics.openStudiosCount)
  }, [summaryMetrics])
  const dayDeltaRub = summaryMetrics ? summaryMetrics.factTodayRub - summaryMetrics.planTodayRub : 0
  const isDayTargetExceeded = dayDeltaRub > 0
  const dayBalanceRub = Math.abs(dayDeltaRub)
  const dayBalancePerStudioRub = summaryMetrics && summaryMetrics.openStudiosCount > 0
    ? Math.round(dayBalanceRub / summaryMetrics.openStudiosCount)
    : 0

  useEffect(() => {
    if (!selectedWeekId || !isWednesdayViewActive) {
      setPulse(null)
      setPulseError(null)
      setResultsLoading(false)
      return
    }

    let cancelled = false
    setResultsLoading(true)
    setPulseError(null)

    getHackathonWednesdayPulse()
      .then((pulseRow) => {
        if (!cancelled) setPulse(pulseRow)
      })
      .catch((err) => {
        if (!cancelled) {
          setPulse(null)
          setPulseError(formatHackathonError(err))
        }
      })
      .finally(() => {
        if (!cancelled) setResultsLoading(false)
      })

    return () => { cancelled = true }
  }, [selectedWeekId, isWednesdayViewActive])

  useEffect(() => {
    if (!selectedWeek?.week_end || !isWednesdayViewActive || studioMetaRows.length === 0) {
      setLiveStudioRevenue([])
      setStudioRevenueError(null)
      setStudioRevenueLoading(false)
      return
    }

    const studioNumbers: string[] = Array.from(new Set(studioMetaRows.map((row) => row.studioNumber).filter(Boolean)))
    if (studioNumbers.length === 0) {
      setLiveStudioRevenue([])
      setStudioRevenueError(null)
      setStudioRevenueLoading(false)
      return
    }

    let cancelled = false
    setStudioRevenueLoading(true)
    const loadLiveStudioRevenue = async () => {
      try {
        const supabase = getSupabase()
        const pageSize = 1000
        const paymentRows: Array<{ studio_number: string | number | null; amount_rub: number | string | null }> = []
        let pageFrom = 0

        // PostgREST/Supabase caps a response page. Fetch every payment row for
        // Wednesday so the studio Top-10 cannot be distorted by the first 1000
        // records only. This follows the same paging pattern used elsewhere in
        // the project for report_payments_with_rub.
        while (true) {
          const { data, error } = await withAuthRecovery(() =>
            (supabase as any)
              .from('report_payments_with_rub')
              .select('studio_number, amount_rub')
              .eq('date', selectedWeek.week_end)
              .in('studio_number', studioNumbers)
              .order('studio_number', { ascending: true })
              .order('id', { ascending: true })
              .range(pageFrom, pageFrom + pageSize - 1)
          ) as {
            data: Array<{ studio_number: string | number | null; amount_rub: number | string | null }> | null
            error: { message?: string } | null
          }
          if (error) throw new Error(error.message || 'Не удалось загрузить выручку студий за сегодня')

          const batch = data ?? []
          paymentRows.push(...batch)
          if (batch.length < pageSize) break
          pageFrom += batch.length
        }

        const amounts = new Map<string, number>()
        for (const row of paymentRows) {
          const studioNumber = row.studio_number == null ? '' : String(row.studio_number)
          if (!studioNumber) continue
          amounts.set(studioNumber, (amounts.get(studioNumber) ?? 0) + Number(row.amount_rub ?? 0))
        }

        const next = studioNumbers.map((studioNumber) => ({
          studioNumber,
          revenueRub: Math.round(amounts.get(studioNumber) ?? 0),
        }))

        if (!cancelled) {
          setLiveStudioRevenue(next)
          setStudioRevenueError(null)
        }
      } catch (err) {
        console.error('Failed to load live studio revenue for hackathon page', err)
        if (!cancelled) {
          setLiveStudioRevenue([])
          setStudioRevenueError(formatHackathonError(err))
        }
      } finally {
        if (!cancelled) setStudioRevenueLoading(false)
      }
    }

    loadLiveStudioRevenue()
    return () => { cancelled = true }
  }, [isWednesdayViewActive, selectedWeek?.week_end, studioMetaRows])

  const handleMove = (partnerNumber: number, segment: AssignmentSegment, nextValue: string) => {
    if (!canEdit) return
    setRows((prev) => prev.map((row) => {
      if (row.partner_number !== partnerNumber || row.segment !== segment) return row

      if (nextValue === 'excluded') {
        return {
          ...row,
          is_participating: false,
          group_number: null,
          assignment_source: 'manual',
        }
      }

      const nextGroup = Number(nextValue)
      if (segment === 'subscription') {
        if (nextGroup !== 5) return row
        return {
          ...row,
          is_participating: true,
          group_number: 5,
          assignment_source: 'auto',
        }
      }

      if (nextGroup < 1 || nextGroup > 4) return row
      return {
        ...row,
        is_participating: true,
        group_number: nextGroup,
        assignment_source: nextGroup === row.suggested_group_number ? 'auto' : 'manual',
      }
    }))
    setSaveMessage(null)
  }

  const handleResetToAuto = () => {
    if (!canEdit) return
    if (hasUnsavedChanges || hasManualRows) {
      const ok = confirm('Перераспределить автоматически? Все ручные изменения и исключения по этой неделе будут сброшены.')
      if (!ok) return
    }

    setRows((prev) => prev.map((row) => ({
      ...row,
      is_participating: true,
      group_number: row.segment === 'subscription' ? 5 : row.suggested_group_number,
      assignment_source: 'auto',
      carried_from_week_start: null,
      carried_from_week_end: null,
    })))
    setSaveMessage(null)
  }

  const handleSave = async () => {
    if (!canEdit) return
    if (!selectedWeekId) return
    setSaving(true)
    setSaveMessage(null)
    try {
      const savedCount = await saveHackathonWeekAssignments({
        weekId: selectedWeekId,
        rows: rows.map((row) => ({
          partner_number: row.partner_number,
          segment: row.segment,
          is_participating: row.is_participating,
          group_number: row.group_number,
          assignment_source: row.assignment_source,
        })),
      })
      const persistedRows = rows.map((row) => ({
        ...row,
        has_saved_assignment: true,
        carried_from_week_start: null,
        carried_from_week_end: null,
      }))
      setRows(persistedRows)
      setInitialRows(persistedRows)
      setSaveMessage(`Сохранено сегментов: ${savedCount}`)
    } catch (err) {
      setError(formatHackathonError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center justify-between gap-3">
        <Button
          variant="outline"
          size="sm"
          className="inline-flex items-center gap-1.5 whitespace-nowrap"
          onClick={() => navigate('/management/personal-managers')}
        >
          <ArrowLeft size={16} className="shrink-0" />
          <span>Назад</span>
        </Button>
        {selectedWeek && (
          <div className="text-right">
            <div className="text-sm font-semibold">Хакатон недели {formatWeekRange(selectedWeek.week_start, selectedWeek.week_end)}</div>
            <div className="text-xs text-muted-foreground">
              Результаты активны {formatDateVerbose(selectedWeek.week_end)}
            </div>
          </div>
        )}
      </div>

      <Card className="p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Trophy size={16} className="text-primary" />
              Результаты хакатона
            </div>
          </div>

          {isWednesdayViewActive && (
            <div className="flex flex-wrap gap-2">
              {(['all', 1, 2, 3, 4, 5] as ResultsScope[]).map((scope) => (
                <button
                  key={String(scope)}
                  type="button"
                  onClick={() => setResultsScope(scope)}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition ${resultsScope === scope ? 'bg-primary text-primary-foreground' : 'border border-border bg-background text-muted-foreground hover:bg-muted'}`}
                >
                  {scope === 'all' ? 'Все' : `Группа ${scope}`}
                </button>
              ))}
            </div>
          )}
        </div>

        {!selectedWeek && (
          <div className="mt-4 text-sm text-muted-foreground">Неделя не выбрана.</div>
        )}

        {selectedWeek && !isWednesdayViewActive && (
          <div className="mt-4 rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-foreground">
            {todayIso < selectedWeek.week_end
              ? `Хакатон ещё не наступил. Результаты появятся ${formatDateVerbose(selectedWeek.week_end)}.`
              : `Результаты хакатона доступны за ${formatDateVerbose(selectedWeek.week_end)}.`}
          </div>
        )}

        {selectedWeek && isWednesdayViewActive && (
          <div className="mt-4 space-y-4">
            {resultsScope === 'all' && pulseError && !resultsLoading && (
              <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-500">
                Не удалось загрузить сводку хакатона: {pulseError}
              </div>
            )}

            {resultsScope === 'all' && summaryMetrics && (
              <div className="relative overflow-hidden rounded-2xl border border-primary/25 bg-gradient-to-r from-primary/10 via-card to-card px-4 py-3 shadow-sm sm:px-5">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/70 to-transparent" />
                <div className="grid grid-cols-1 gap-3 xl:grid-cols-[174px_minmax(0,1.15fr)_minmax(0,0.82fr)_minmax(0,1fr)] xl:items-center xl:gap-0">
                  <div className="flex items-center justify-center xl:justify-start xl:pr-4">
                    <RatingGauge
                      percent={dayGaugePct}
                      title="цель на сегодня"
                    />
                  </div>

                  <div className="min-w-0 border-t border-border/50 pt-3 xl:border-l xl:border-t-0 xl:py-1 xl:pl-7 xl:pr-8">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                        <span>Неделя</span>
                      </div>
                      <div className="text-2xl font-bold tabular-nums text-foreground">{Math.round(weekCompletionPct)}%</div>
                    </div>
                    <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-muted/80">
                      <div
                        className="h-full rounded-full bg-primary transition-[width] duration-300"
                        style={{ width: `${Math.min(100, weekCompletionPct)}%` }}
                      />
                    </div>
                    <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
                      <span className="font-semibold tabular-nums">{formatCurrency(summaryMetrics.factWeekRub)}</span>
                      <span className="text-muted-foreground">из {formatCurrency(summaryMetrics.planWeekRub)}</span>
                    </div>
                  </div>

                  <div className="min-w-0 border-t border-border/50 pt-3 xl:border-l xl:border-t-0 xl:py-1 xl:px-8">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                      <span>Нужно сегодня</span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-5">
                      <div className="min-w-0">
                        <div className="text-[11px] text-muted-foreground">На сеть</div>
                        <div className="mt-1 whitespace-nowrap text-lg font-semibold tabular-nums">{formatCurrency(summaryMetrics.planTodayRub)}</div>
                      </div>
                      <div className="min-w-0">
                        <div className="text-[11px] text-muted-foreground">На студию</div>
                        <div className="mt-1 whitespace-nowrap text-lg font-semibold tabular-nums">{formatCurrency(dayTargetPerStudioRub)}</div>
                      </div>
                    </div>
                  </div>

                  <div className="min-w-0 border-t border-border/50 pt-3 xl:border-l xl:border-t-0 xl:py-1 xl:pl-8">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                      <span>Сейчас</span>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3 xl:gap-5">
                      <div className="min-w-0">
                        <div className="text-[11px] text-muted-foreground">Сделано</div>
                        <div className="mt-1 whitespace-nowrap text-lg font-semibold tabular-nums">{formatCurrency(summaryMetrics.factTodayRub)}</div>
                      </div>
                      <div className="min-w-0">
                        <div className="text-[11px] text-muted-foreground">{isDayTargetExceeded ? 'Сверх цели' : 'Осталось'}</div>
                        <div className={`mt-1 whitespace-nowrap text-lg font-semibold tabular-nums ${isDayTargetExceeded ? 'text-emerald-500' : 'text-foreground'}`}>
                          {isDayTargetExceeded ? '+' : ''}{formatCurrency(dayBalanceRub)}
                        </div>
                      </div>
                      <div className="min-w-0">
                        <div className="text-[11px] text-muted-foreground">На студию</div>
                        <div className={`mt-1 whitespace-nowrap text-lg font-semibold tabular-nums ${isDayTargetExceeded ? 'text-emerald-500' : 'text-foreground'}`}>
                          {isDayTargetExceeded ? '+' : ''}{formatCurrency(dayBalancePerStudioRub)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {resultsScope === 'all' && !summaryMetrics && !resultsLoading && !pulseError && (
              <div className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
                Данные за сегодня пока не поступили.
              </div>
            )}

            {resultsScope === 'all' ? (
              <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                <div className="rounded-xl border border-border/70 bg-card p-3 shadow-none">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold">Топ-10 франчайзи за сегодня</div>
                    <div className="text-[11px] text-muted-foreground">по выручке за сегодня</div>
                  </div>
                  <div className="space-y-2">
                    {topPartnerRows.map((row, index) => (
                      <div key={`top-partner-${row.partner_number}`} className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-background/40 px-3 py-2.5">
                        <div className="flex min-w-0 items-center gap-3">
                          <div className={`inline-flex h-7 min-w-7 shrink-0 items-center justify-center rounded-full border text-sm font-semibold ${getRankBadgeClass(index + 1)}`}>
                            {index + 1}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium">{row.persons_fio}</div>
                            <div className="truncate text-[11px] text-muted-foreground">
                              {resultPartnerCitiesByNumber.get(row.partner_number) ?? 'Город не указан'}
                            </div>
                          </div>
                        </div>
                        <div className="shrink-0 text-sm font-semibold">{formatCurrency(row.revenue_rub)}</div>
                      </div>
                    ))}
                    {rankingLoading && (
                      <div className="rounded-xl border border-dashed border-border px-4 py-4 text-sm text-muted-foreground">
                        Загрузка рейтинга франчайзи...
                      </div>
                    )}
                    {rankingError && !rankingLoading && (
                      <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-4 text-sm text-red-500">
                        Не удалось загрузить рейтинг франчайзи: {rankingError}
                      </div>
                    )}
                    {topPartnerRows.length === 0 && !rankingError && !rankingLoading && (
                      <div className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
                        Для этого среза пока нет данных.
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-xl border border-border/70 bg-card p-3 shadow-none">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold">Топ-10 студий за сегодня</div>
                    <div className="text-[11px] text-muted-foreground">по выручке за сегодня</div>
                  </div>
                  <div className="space-y-2">
                    {topStudioRows.map((row, index) => (
                      <div key={`top-studio-${row.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-background/40 px-3 py-2.5">
                        <div className="flex min-w-0 items-center gap-3">
                          <div className={`inline-flex h-7 min-w-7 shrink-0 items-center justify-center rounded-full border text-sm font-semibold ${getRankBadgeClass(index + 1)}`}>
                            {index + 1}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium">{row.label}</div>
                            {row.meta && (
                              <div className="truncate text-[11px] text-muted-foreground">{row.meta}</div>
                            )}
                          </div>
                        </div>
                        <div className="shrink-0 text-sm font-semibold">{formatCurrency(row.value)}</div>
                      </div>
                    ))}
                    {rankingLoading && (
                      <div className="rounded-xl border border-dashed border-border px-4 py-4 text-sm text-muted-foreground">
                        Загрузка рейтинга студий...
                      </div>
                    )}
                    {rankingError && !rankingLoading && (
                      <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-4 text-sm text-red-500">
                        Не удалось загрузить рейтинг студий: {rankingError}
                      </div>
                    )}
                    {topStudioRows.length === 0 && !rankingError && !rankingLoading && (
                      <div className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
                        Для этого среза пока нет данных.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <div className="rounded-xl border border-border/70 bg-card p-3 shadow-none">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold">Франчайзи: группа {resultsScope}</div>
                    <div className="text-[11px] text-muted-foreground">выручка за сегодня</div>
                  </div>
                  <div className="space-y-3">
                    {displayedPartnerResultsScoped.map((row) => {
                      const widthPct = partnerChartMax > 0 ? (row.revenue_rub / partnerChartMax) * 100 : 0
                      return (
                        <div key={`partner-bar-${row.partner_number}`} className="space-y-1.5">
                          <div className="flex items-center justify-between gap-3 text-xs">
                            <div className="flex min-w-0 items-center gap-2.5">
                              <div className={`inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold ${getRankBadgeClass(row.place_in_group)}`}>
                                {row.place_in_group}
                              </div>
                              <div className="min-w-0">
                                <div className="truncate font-medium">{row.persons_fio}</div>
                                <div className="truncate text-[11px] text-muted-foreground">
                                  {resultPartnerCitiesByNumber.get(row.partner_number) ?? 'Город не указан'}
                                </div>
                              </div>
                            </div>
                            <div className="shrink-0 font-medium">{formatCurrency(row.revenue_rub)}</div>
                          </div>
                          <div className="h-2.5 rounded-full bg-primary/10">
                            <div className="h-2.5 rounded-full bg-primary" style={{ width: `${Math.max(widthPct, row.revenue_rub === 0 ? 0 : 4)}%` }} />
                          </div>
                        </div>
                      )
                    })}
                    {rankingLoading && (
                      <div className="rounded-xl border border-dashed border-border px-4 py-4 text-sm text-muted-foreground">
                        Загрузка рейтинга франчайзи...
                      </div>
                    )}
                    {rankingError && !rankingLoading && (
                      <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-4 text-sm text-red-500">
                        Не удалось загрузить рейтинг франчайзи: {rankingError}
                      </div>
                    )}
                    {displayedPartnerResultsScoped.length === 0 && !rankingError && !rankingLoading && (
                      <div className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
                        Для этой группы пока нет данных.
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-xl border border-border/70 bg-card p-3 shadow-none">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold">Студии: группа {resultsScope}</div>
                    <div className="text-[11px] text-muted-foreground">выручка за сегодня</div>
                  </div>
                  <div className="space-y-3">
                    {displayedStudioBars.map((row, index) => {
                      const widthPct = studioChartMax > 0 ? (row.value / studioChartMax) * 100 : 0
                      return (
                        <div key={`studio-bar-${row.id}`} className="space-y-1.5">
                          <div className="flex items-center justify-between gap-3 text-xs">
                            <div className="flex min-w-0 items-center gap-2.5">
                              <div className={`inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold ${getRankBadgeClass(index + 1)}`}>
                                {index + 1}
                              </div>
                              <div className="min-w-0">
                                <div className="truncate font-medium">{row.label}</div>
                                {row.meta && (
                                  <div className="truncate text-[11px] text-muted-foreground">{row.meta}</div>
                                )}
                              </div>
                            </div>
                            <div className="shrink-0 font-medium">{formatCurrency(row.value)}</div>
                          </div>
                          <div className="h-2.5 rounded-full bg-muted">
                            <div className="h-2.5 rounded-full bg-muted-foreground/50" style={{ width: `${Math.max(widthPct, row.value === 0 ? 0 : 4)}%` }} />
                          </div>
                        </div>
                      )
                    })}
                    {rankingLoading && (
                      <div className="rounded-xl border border-dashed border-border px-4 py-4 text-sm text-muted-foreground">
                        Загрузка рейтинга студий...
                      </div>
                    )}
                    {rankingError && !rankingLoading && (
                      <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-4 text-sm text-red-500">
                        Не удалось загрузить рейтинг студий: {rankingError}
                      </div>
                    )}
                    {displayedStudioBars.length === 0 && !rankingError && !rankingLoading && (
                      <div className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
                        Для этой группы пока нет данных.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

          </div>
        )}
      </Card>

      <Card className="overflow-hidden border-border/80">
        <button
          type="button"
          onClick={() => setDistributionOpen((prev) => !prev)}
          className="flex w-full items-center justify-between gap-3 bg-card px-4 py-3 text-left transition hover:bg-muted/30"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-semibold">
              Распределение
              <span
                title={[
                  'Распределение считается по 4 завершённым неделям до выбранной недели.',
                  'Учитываются только OPEN-студии.',
                  'Студии на абонементах распределяются в группы 1-4 по количеству и средней выручке.',
                  'Студии на подписках, включая MIXED, участвуют в группе 5.',
                  'Если у франчайзи есть и студии на абонементах, и студии на подписках, он одновременно участвует в двух сегментах: абонементный — в своей группе 1-4, подписочный — в группе 5.',
                  'Каждый сегмент можно исключить независимо. Если два сегмента одного франчайзи находятся в одном разделе, они показываются одним блоком.',
                  'В рейтинге конкретной группы учитывается только выручка студий соответствующего сегмента.',
                ].join('\n')}
                className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-border text-muted-foreground"
              >
                <Info size={12} />
              </span>
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              Участников: {participantsCount} · исключено сегментов: {excludedCount} · ручных: {manualCount}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {!distributionOpen && selectedWeek && (
              <span className="hidden rounded-full border border-primary/30 bg-primary/10 px-2 py-1 text-[11px] font-medium text-primary sm:inline-flex">
                {formatWeekRange(selectedWeek.week_start, selectedWeek.week_end)}
              </span>
            )}
            <ChevronDown size={16} className={`transition ${distributionOpen ? 'rotate-180' : ''}`} />
          </div>
        </button>

        {distributionOpen && (
          <div className="border-t border-border/70 px-3 py-3">
            {!canEdit && (
              <div className="mb-3 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-xs text-foreground">
                Распределение доступно для просмотра. Менять группы и сохранять может только руководитель.
              </div>
            )}
            <div className="mb-3 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                {selectedWeek && (
                  <div className="flex h-8 items-center rounded-lg border border-primary/30 bg-primary/10 px-3 text-sm font-medium text-foreground">
                    Неделя: {formatWeekRange(selectedWeek.week_start, selectedWeek.week_end)}
                  </div>
                )}
                <select
                  value={selectedManagerId}
                  onChange={(event) => setSelectedManagerId(event.target.value)}
                  className="h-8 rounded-lg border border-border bg-background px-3 text-sm"
                >
                  <option value="all">Все ЛМ</option>
                  {managerOptions.map((manager) => (
                    <option key={manager.manager_user_id} value={manager.manager_user_id}>
                      {managerDisplayName(manager)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-row flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap"
                  onClick={handleResetToAuto}
                  disabled={!canEdit || rows.length === 0}
                >
                  <RefreshCcw size={16} className="shrink-0" />
                  <span>Автораспределить</span>
                </Button>
                <Button
                  size="sm"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap"
                  onClick={handleSave}
                  disabled={!canEdit || saving || !hasUnsavedChanges || !selectedWeekId}
                >
                  <Save size={16} className="shrink-0" />
                  <span>{saving ? 'Сохраняю...' : 'Сохранить'}</span>
                </Button>
              </div>
            </div>

            {carriedFromRow?.carried_from_week_start && carriedFromRow.carried_from_week_end && (
              <div className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-foreground">
                Распределение перенесено с недели {formatWeekRange(carriedFromRow.carried_from_week_start, carriedFromRow.carried_from_week_end)}.
                {canEdit && ' Нажмите «Сохранить», чтобы закрепить его за этой неделей, или «Автораспределить», чтобы пересчитать заново.'}
              </div>
            )}
            {saveMessage && (
              <div className="mb-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-600">
                {saveMessage}
              </div>
            )}
            {error && (
              <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-500">
                {error}
              </div>
            )}

            {loading ? (
              <div className="rounded-lg border border-dashed border-border px-3 py-4 text-sm text-muted-foreground">Загрузка распределения...</div>
            ) : (
              <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
                {GROUP_DEFS.map((group) => {
                  const groupRows = groupedRows.get(group.key) ?? []
                  const partnerCards = groupAssignmentRowsByPartner(groupRows)
                  const groupManualCount = groupRows.filter((row) => row.assignment_source === 'manual').length

                  return (
                    <Card key={String(group.key)} className="border-border/80 bg-card p-3 shadow-none">
                      <div className="mb-2 flex items-start justify-between gap-2 border-l-2 border-primary pl-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <div className="truncate text-sm font-semibold">{group.title}</div>
                            <span
                              title={group.hint}
                              className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-border text-muted-foreground"
                            >
                              <Info size={10} />
                            </span>
                          </div>
                          <div className="text-[10px] text-muted-foreground">
                            {groupManualCount > 0 ? `Ручных: ${groupManualCount}` : 'Авто'}
                          </div>
                        </div>
                        <div className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                          {partnerCards.length}
                        </div>
                      </div>

                      <div className="space-y-1">
                        {partnerCards.length === 0 && (
                          <div className="rounded-lg border border-dashed border-border px-3 py-3 text-center text-xs text-muted-foreground">
                            Пусто
                          </div>
                        )}

                        {partnerCards.map((partnerCard) => (
                          <div key={partnerCard.partnerNumber} className="rounded-lg border border-border/80 bg-background/40 px-2.5 py-1.5">
                            <div className="truncate text-[13px] font-medium leading-4">{partnerCard.personsFio}</div>
                            <div className="mt-0.5 min-w-0 space-y-0.5">
                              {partnerCard.rows.map((row) => {
                                const segmentLabel = row.segment === 'subscription'
                                  ? 'Студии на подписках'
                                  : 'Студии на абонементах'
                                const segmentSummary = `${segmentLabel}: ${formatStudioCount(row.open_studio_count)} · ${formatCurrency(row.avg_weekly_revenue_per_studio_rub)} на студию в неделю`

                                return (
                                  <div key={`${row.partner_number}:${row.segment}`} className="flex min-h-6 min-w-0 items-center gap-2">
                                    <div
                                      title={`${segmentSummary}. ${REVENUE_HINT}`}
                                      className="min-w-0 flex-1 truncate text-[11px] leading-4 text-muted-foreground"
                                    >
                                      {segmentSummary}
                                    </div>
                                    <select
                                      value={row.is_participating ? String(row.group_number) : 'excluded'}
                                      onChange={(event) => handleMove(row.partner_number, row.segment, event.target.value)}
                                      disabled={!canEdit}
                                      className="h-6 w-[72px] shrink-0 self-center rounded-md border border-border bg-background px-2 text-[11px] font-medium"
                                    >
                                      {row.segment === 'subscription' ? (
                                        <option value="5">Г5</option>
                                      ) : (
                                        <>
                                          <option value="1">Г1</option>
                                          <option value="2">Г2</option>
                                          <option value="3">Г3</option>
                                          <option value="4">Г4</option>
                                        </>
                                      )}
                                      <option value="excluded">Неуч.</option>
                                    </select>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  )
}
